import type { ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { PageLoader } from '@shared/components/PageLoader';
import { env } from '@/config/env';
import { useOnboarding } from '../hooks/useOnboarding';

/**
 * Global onboarding guard. Sits inside ProtectedRoute (auth already resolved)
 * and routes the user based on GET /billing/onboarding/ → `next_step`:
 *
 *   add_station → /onboarding  (guided flow, station step active)
 *   subscribe   → /onboarding  (guided flow, activation step active)
 *   completed   → render the app as usual
 *
 * Both pending steps now lead to the single unified onboarding page, which
 * detects the current state and expands the right step. Fails OPEN: if billing is
 * disabled, the query is loading-then-erroring, or the backend reports billing
 * off, we let the user through. The guard never blocks reads — it only steers an
 * org that still needs to finish setup. The onboarding and billing routes are
 * exempt so the user can actually complete each step (and so the Stripe
 * success/cancel returns land correctly).
 */
export const OnboardingGate = ({ children }: { children: ReactNode }) => {
  const { lng = 'es' } = useParams();
  const location = useLocation();
  const { data, isLoading, isError } = useOnboarding();

  if (!env.billingEnabled) {
    return <>{children}</>;
  }

  if (isLoading) {
    return <PageLoader />;
  }

  // Don't let a billing-endpoint hiccup lock anyone out of the whole app.
  if (isError || !data || !data.billing_enabled) {
    return <>{children}</>;
  }

  // Strip the leading `/{lng}/` to compare against app-relative paths.
  const relativePath = location.pathname.replace(/^\/[^/]+\/?/, '');
  const isExempt = relativePath.startsWith('onboarding') || relativePath.startsWith('billing');

  if (data.next_step === 'completed' || isExempt) {
    return <>{children}</>;
  }

  return <Navigate to={`/${lng}/onboarding`} replace />;
};
